import React, { useState, memo } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Copy, Check, MessageSquare, Smartphone, FileText, Sparkles, Zap, Brain, History, ChevronDown, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
function Accordion({
  title,
  icon,
  children
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-border/50 rounded-xl overflow-hidden" data-id="element-1342">
      <button className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-secondary/30 transition-colors" onClick={() => setOpen(!open)} data-id="element-1343">
        <span className="flex items-center gap-2 font-medium text-base" data-id="element-1344">
          {icon}
          {title}
        </span>
        <ChevronDown size={18} className={`text-muted-foreground transition-transform duration-200 flex-shrink-0 ${open ? 'rotate-180' : ''}`} data-id="element-1345" />
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-border/30" data-id="element-1346">
          {children}
        </div>}
    </div>;
}
export function TextLandingPage() {
  const [copied, setCopied] = useState(false);
  const phoneNumber = '(774) 472-7423';
  const smsHref = `sms:+1${phoneNumber.replace(/\D/g, '')}?body=Hi%20ScribeTok!%20I%20want%20to%20transcribe%20a%20video.`;
  const handleCopyNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div className="min-h-screen w-full bg-background flex flex-col items-center justify-center p-4 sm:p-6 overflow-x-hidden" data-id="element-1347">
      <div className="max-w-md w-full mx-auto text-center" data-id="element-1348">
        {/* Logo */}
        <div className="flex justify-center mb-6" data-id="element-1349">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tsscMEhsZ3L4JwT2r374e/ScribeTok-logo.png" alt="ScribeTok Logo" className="h-14 w-auto" data-id="element-1350" />
        </div>

        {/* Headline */}
        <h1 className="text-3xl md:text-4xl font-bold mb-3 animate-slide-down" data-id="element-1351">
          The Reading Mode{' '}
          <span className="text-primary" data-id="element-1352">TikTok Never Gave You</span>
        </h1>

        {/* Subhead */}
        <p className="text-lg text-muted-foreground mb-2 animate-slide-down" style={{
        animationDelay: '100ms'
      }} data-id="element-1353">
          Text a link. We'll send back a transcript plus takeaways, quotes, and
          a draft reply.
        </p>
        <p className="text-sm text-muted-foreground mb-8" data-id="element-1354">
          Works with TikTok, Reels, YouTube, and public videos.
        </p>

        {/* Mobile: full-width primary CTA */}
        <a href={smsHref} className="block md:hidden mb-4 animate-fade-in" style={{
        animationDelay: '150ms'
      }} data-id="element-1355">
          <Button size="lg" className="w-full text-base" data-id="element-1356">
            <MessageSquare size={18} className="mr-2 flex-shrink-0" data-id="element-1357" />
            Open Messages
          </Button>
        </a>

        {/* Desktop: number copy + button side by side */}
        <div className="hidden md:flex items-center gap-3 justify-center mb-4 animate-fade-in" style={{
        animationDelay: '150ms'
      }} data-id="element-1358">
          <div className="flex items-center gap-2 px-5 py-3 bg-secondary/70 rounded-full cursor-pointer hover:bg-secondary/90 transition-colors" onClick={handleCopyNumber} data-id="element-1359">
            <span className="font-medium" data-id="element-1360">{phoneNumber}</span>
            {copied ? <span className="flex items-center gap-1 text-success text-xs font-medium" data-id="element-1361">
                <Check size={13} data-id="element-1362" /> Copied!
              </span> : <span className="flex items-center gap-1 text-muted-foreground text-xs" data-id="element-1363">
                <Copy size={13} data-id="element-1364" /> Copy
              </span>}
          </div>
          <a href={smsHref} data-id="element-1365">
            <Button size="lg" className="text-base px-7" data-id="element-1366">
              <MessageSquare size={18} className="mr-2 flex-shrink-0" data-id="element-1367" />
              Open Messages
            </Button>
          </a>
        </div>

        {/* Desktop: QR */}
        <div className="hidden md:flex flex-col items-center mb-6" data-id="element-1368">
          <img src="https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=sms:+17744727423" alt="SMS QR Code" className="rounded-lg shadow-sm mb-1" width={120} height={120} data-id="element-1369" />
          <p className="text-xs text-muted-foreground" data-id="element-1370">Or scan to open SMS</p>
        </div>

        {/* Mobile: number copy pill */}
        <div className="flex md:hidden items-center gap-2 px-4 py-2 bg-secondary/70 rounded-full cursor-pointer mx-auto w-fit mb-6 hover:bg-secondary/90 transition-colors" onClick={handleCopyNumber} data-id="element-1371">
          <span className="font-medium text-sm" data-id="element-1372">{phoneNumber}</span>
          {copied ? <span className="flex items-center gap-1 text-success text-xs font-medium" data-id="element-1373">
              <Check size={13} data-id="element-1374" /> Copied!
            </span> : <span className="flex items-center gap-1 text-muted-foreground text-xs" data-id="element-1375">
              <Copy size={13} data-id="element-1376" /> Copy
            </span>}
        </div>

        {/* Trust strip */}
        <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center text-sm text-muted-foreground mb-8" data-id="element-1377">
          <span className="flex items-center gap-1.5" data-id="element-1378">
            <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-1379" />
            Public videos only
          </span>
          <span className="flex items-center gap-1.5" data-id="element-1380">
            <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-1381" />
            You text first — we don't spam
          </span>
          <span className="flex items-center gap-1.5" data-id="element-1382">
            <CheckCircle size={14} className="text-success flex-shrink-0" data-id="element-1383" />
            Reply STOP to opt out
          </span>
          <span className="flex items-center gap-1.5 text-muted-foreground/80" data-id="element-1384">
            Msg &amp; data rates may apply
          </span>
        </div>

        {/* Demo snippet — numbered menu */}
        <div className="mb-10 animate-fade-in" style={{
        animationDelay: '250ms'
      }} data-id="element-1385">
          <p className="text-xs font-medium text-muted-foreground mb-3 uppercase tracking-wide" data-id="element-1386">
            See it in action
          </p>
          <div className="bg-secondary/20 rounded-2xl p-4 border border-border/20 space-y-3 text-left" data-id="element-1387">
            <div className="flex justify-end" data-id="element-1388">
              <div className="tiktok-gradient text-white px-4 py-2.5 rounded-2xl max-w-[240px] text-sm shadow-sm break-words" data-id="element-1389">
                https://tiktok.com/@user/video/123
              </div>
            </div>
            <div className="flex justify-start" data-id="element-1390">
              <div className="bg-foreground text-background px-4 py-2.5 rounded-2xl max-w-[260px] text-sm shadow-sm break-words" data-id="element-1391">
                <p className="flex items-start gap-1.5 mb-2" data-id="element-1392">
                  <span className="flex-shrink-0" data-id="element-1393">✅</span>
                  <span className="font-medium" data-id="element-1394">Transcript ready!</span>
                </p>
                <p className="ml-5 text-xs opacity-80 mb-3" data-id="element-1395">
                  "Today I'm showing three productivity hacks that changed my
                  life..."
                </p>
                <p className="ml-5 text-xs font-medium mb-1" data-id="element-1396">
                  Reply with a number:
                </p>
                <div className="ml-5 space-y-0.5 text-xs" data-id="element-1397">
                  <p data-id="element-1398">1️⃣ Key takeaways</p>
                  <p data-id="element-1399">2️⃣ Best quote</p>
                  <p data-id="element-1400">3️⃣ Draft a reply</p>
                  <p className="opacity-70 mt-1" data-id="element-1401">Or just ask a question</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-1402">
              <div className="tiktok-gradient text-white px-4 py-2.5 rounded-2xl text-sm shadow-sm" data-id="element-1403">
                1
              </div>
            </div>
            <div className="flex justify-start" data-id="element-1404">
              <div className="bg-foreground text-background px-4 py-2.5 rounded-2xl max-w-[260px] text-sm shadow-sm break-words" data-id="element-1405">
                <p className="flex items-start gap-1.5 mb-1" data-id="element-1406">
                  <span data-id="element-1407">📝</span>
                  <span className="font-medium" data-id="element-1408">Key takeaways:</span>
                </p>
                <ul className="ml-5 text-xs space-y-1 opacity-90" data-id="element-1409">
                  <li data-id="element-1410">• Time blocking beats to-do lists</li>
                  <li data-id="element-1411">• Two-minute rule for small tasks</li>
                  <li data-id="element-1412">• Digital detox every evening</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible sections */}
        <div className="space-y-3 mb-10 text-left animate-fade-in" style={{
        animationDelay: '300ms'
      }} data-id="element-1413">
          <Accordion title="How it works" icon={<Zap size={16} className="text-primary" data-id="element-1415" />} data-id="element-1414">
            <ol className="space-y-4 pt-2" data-id="element-1416">
              <li className="flex items-start gap-3" data-id="element-1417">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs flex-shrink-0 mt-0.5 font-medium" data-id="element-1418">
                  1
                </div>
                <div data-id="element-1419">
                  <p className="font-medium text-sm" data-id="element-1420">Text any video link</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1421">
                    Send a TikTok, Reel, or YouTube URL to {phoneNumber}. No
                    account needed.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3" data-id="element-1422">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs flex-shrink-0 mt-0.5 font-medium" data-id="element-1423">
                  2
                </div>
                <div data-id="element-1424">
                  <p className="font-medium text-sm" data-id="element-1425">Get your transcript</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1426">
                    Readable content delivered in seconds, with a shareable
                    link.
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3" data-id="element-1427">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-xs flex-shrink-0 mt-0.5 font-medium" data-id="element-1428">
                  3
                </div>
                <div data-id="element-1429">
                  <p className="font-medium text-sm" data-id="element-1430">Reply to get more</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1431">
                    Choose takeaways, a quote, a draft reply — or ask your own
                    question.
                  </p>
                </div>
              </li>
            </ol>
          </Accordion>

          <Accordion title="What you get" icon={<Sparkles size={16} className="text-primary" data-id="element-1433" />} data-id="element-1432">
            <div className="space-y-3 pt-2" data-id="element-1434">
              <div className="flex items-start gap-3" data-id="element-1435">
                <FileText size={15} className="text-primary mt-0.5 flex-shrink-0" data-id="element-1436" />
                <div data-id="element-1437">
                  <p className="font-medium text-sm" data-id="element-1438">Full transcript</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1439">
                    Every word, readable and shareable.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1440">
                <Brain size={15} className="text-primary mt-0.5 flex-shrink-0" data-id="element-1441" />
                <div data-id="element-1442">
                  <p className="font-medium text-sm" data-id="element-1443">Key takeaways</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1444">
                    3–5 bullets capturing what actually matters.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1445">
                <Sparkles size={15} className="text-primary mt-0.5 flex-shrink-0" data-id="element-1446" />
                <div data-id="element-1447">
                  <p className="font-medium text-sm" data-id="element-1448">Best quote</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1449">
                    The most memorable line, ready to share.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1450">
                <MessageSquare size={15} className="text-primary mt-0.5 flex-shrink-0" data-id="element-1451" />
                <div data-id="element-1452">
                  <p className="font-medium text-sm" data-id="element-1453">Draft reply</p>
                  <p className="text-sm text-muted-foreground" data-id="element-1454">
                    A thoughtful response you can send or edit.
                  </p>
                </div>
              </div>
            </div>
          </Accordion>

          <Accordion title="Commands" icon={<Smartphone size={16} className="text-primary" data-id="element-1456" />} data-id="element-1455">
            <div className="space-y-3 pt-2" data-id="element-1457">
              {[{
              cmd: '/tldr',
              desc: 'Key takeaways from the last transcript'
            }, {
              cmd: '/quote',
              desc: 'Best quote from the last transcript'
            }, {
              cmd: '/chat',
              desc: 'Ask a question about the transcript'
            }, {
              cmd: '/vault',
              desc: 'Your 5 most recent transcripts'
            }, {
              cmd: '/help',
              desc: 'List all available commands'
            }].map(({
              cmd,
              desc
            }) => <div key={cmd} className="flex items-start gap-3" data-id="element-1458">
                  <code className="text-primary text-sm font-mono bg-primary/10 px-2 py-0.5 rounded flex-shrink-0" data-id="element-1459">
                    {cmd}
                  </code>
                  <p className="text-sm text-muted-foreground" data-id="element-1460">{desc}</p>
                </div>)}
            </div>
          </Accordion>

          <Accordion title="Privacy & trust" icon={<AlertCircle size={16} className="text-primary" data-id="element-1462" />} data-id="element-1461">
            <div className="space-y-3 pt-2 text-sm text-muted-foreground" data-id="element-1463">
              <p data-id="element-1464">
                ScribeTok only processes public videos. We never access private
                accounts or content.
              </p>
              <p data-id="element-1465">
                Your transcripts are saved to your phone number. No account
                required to get started.
              </p>
              <p data-id="element-1466">
                We don't sell your data. We don't send unsolicited messages.
              </p>
              <p data-id="element-1467">
                By texting us, you consent to receive automated reply messages.
                Reply STOP at any time to opt out.{' '}
                <Link to="/text/compliance" className="text-primary hover:underline" data-id="element-1468">
                  View full terms
                </Link>
              </p>
            </div>
          </Accordion>
        </div>

        {/* Footer */}
        <p className="text-xs text-muted-foreground border-t border-border/50 pt-4" data-id="element-1469">
          ScribeTok is a user-initiated SMS service. We do not send promotional
          messages. You can opt out at any time by replying STOP. Msg &amp; data
          rates may apply.
        </p>
      </div>
    </div>;
}